import React, { useEffect, useRef, useState } from 'react';
import Header from '../../components/Header';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Input,
  List,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  VStack
} from '@chakra-ui/react';
import { CheckCircleIcon, InfoIcon, WarningIcon, DownloadIcon } from '@chakra-ui/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API from '../../helpers/axiosInstance';
import { fetchWrapper } from '../../helpers';
import { LuArrowLeft, LuUpload } from 'react-icons/lu';
import {  } from '../../utils/DefaultFunction';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';

const ConfigurePushNotification = ({ header, embedded }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('idle');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  let user = JSON.parse(localStorage.getItem('customer'));

  // The banner shown here must reflect the real status regardless of how this page
  // was reached — the ConfigBanner button passes `isConfigured` via location.state,
  // but a direct sidebar link (or a page refresh) has no state at all. Fetch the
  // same /get-sdk-key data ConfigBanner itself uses and prefer that once it's in,
  // falling back to the passed-in state only for the very first render.
  const [sdkKeyData, setSdkKeyData] = useState(null);
  const locationIsConfigured = useLocation()?.state?.isConfigured;
  const isConfigured = sdkKeyData ? sdkKeyData?.android === 1 : locationIsConfigured;

  useEffect(() => {
    const loadSdkKey = async () => {
      try {
        const res = await fetchWrapper.get(`${process.env.REACT_APP_API_URL}/customer/get-sdk-key`);
        setSdkKeyData(res || {});
      } catch (error) {
        console.error('Error fetching SDK key status:', error);
      }
    };
    loadSdkKey();
  }, []);

  const [loading, setLoading] = useState(false);


  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const isJsonFile = file.type === 'application/json' || file.name.toLowerCase().endsWith('.json');

    if (isJsonFile) {
      setSelectedFile(file);
      setUploadStatus('success');
      handleFileUpload(file);
    } else {
      setUploadStatus('error');
      setSelectedFile(null);
      toast.error('Invalid file format. Please upload a JSON file', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleFileUpload = async (file) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('jsonFile', file);

      const url = `${process.env.REACT_APP_API_URL_XPUSH}/upload-service-file`;
      await API.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-api-key": localStorage.getItem('api-key'),
          "x-access-token": localStorage.getItem('customer-portal')
        }
      });

      toast.success(`File ${file.name} uploaded successfully!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

    } catch (error) {
      toast.error(error.message || 'There was an error uploading your file', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setUploadStatus('error');
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleBack = () => {
    navigate(-1);
  };

  const content = (
            <VStack spacing={8} align="stretch">
              {/* Same banner ConfigBanner shows inline in the SDK docs — repeated here
                  since this page is also reachable directly (sidebar quick link),
                  where there's no location.state to fall back on. */}
              <Box
                borderRadius="8.75px"
                border="1px solid"
                borderColor={isConfigured ? '#00c951' : '#ff8904'}
                bg={isConfigured ? '#f0fdf4' : '#fff7ed'}
                p={4}
              >
                <HStack spacing={3} align="center">
                  <Icon
                    as={isConfigured ? CheckCircleIcon : InfoIcon}
                    color={isConfigured ? 'green.500' : 'orange.500'}
                    boxSize={5}
                  />
                  <Box>
                    <Text fontSize="md" fontWeight="semibold" color={isConfigured ? 'green.800' : 'orange.800'} mb={1}>
                      Push Notification {isConfigured ? 'Configured' : 'Not Configured'}
                    </Text>
                    <Text fontSize="sm" color={isConfigured ? 'green.700' : 'orange.700'}>
                      {isConfigured
                        ? 'Your push notification service is successfully configured and ready to use.'
                        : 'Configure your push notification service to enable real-time notifications.'}
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {/* Introduction */}
              <Box>
                <Text fontSize="xl" mb={4}>Introduction</Text>
                <Text mb={4}>
                  We utilize Firebase's efficient background services to seamlessly wake up our application and establish a connection with our Push Notification Server. Once the connection is successfully established, all push notifications are delivered directly from our server, ensuring complete control over message content and delivery.
                </Text>
                <Text>
                  By leveraging Firebase's deep OS-level integration, we optimize battery usage while maintaining real-time connectivity. This approach minimizes unnecessary wake-ups and ensures that notifications are delivered reliably without impacting device performance. Our system is designed to provide a smooth and efficient push notification experience while maintaining user privacy and maximizing battery efficiency.
                </Text>
              </Box>

              {/* Upload Section */}
              <Box>
                <Text fontSize="xl" mb={4}>Upload Firebase Service Account JSON File</Text>

                {/* Drag & Drop Upload Area */}
                <Box
                  border="2px dashed"
                  borderColor={
                    isDragging
                      ? 'blue.400'
                      : uploadStatus === 'success'
                        ? 'green.500'
                        : uploadStatus === 'error'
                          ? 'red.500'
                          : 'gray.300'
                  }
                  bg={
                    isDragging
                      ? 'blue.50'
                      : uploadStatus === 'success'
                        ? 'green.50'
                        : uploadStatus === 'error'
                          ? 'red.50'
                          : 'white'
                  }
                  borderRadius="lg"
                  p={8}
                  textAlign="center"
                  transition="all 0.2s"
                  _hover={{ borderColor: uploadStatus === 'idle' ? 'blue.400' : undefined }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  cursor="pointer"
                  onClick={uploadStatus === 'idle' ? handleUploadClick : undefined}
                >
                  {uploadStatus === 'success' && selectedFile ? (
                    <VStack spacing={3}>
                      <Icon as={CheckCircleIcon} w={12} h={12} color="green.600" />
                      <Box>
                        <Text color="green.800" mb={1} fontWeight="semibold">
                          File uploaded successfully!
                        </Text>
                        <Text color="green.700">{selectedFile.name}</Text>
                      </Box>
                      <Button
                        variant="link"
                        color="blue.500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUploadClick();
                        }}
                      >
                        Replace file
                      </Button>
                    </VStack>
                  ) : uploadStatus === 'error' ? (
                    <VStack spacing={3}>
                      <Icon as={WarningIcon} w={12} h={12} color="red.600" />
                      <Box>
                        <Text color="red.800" mb={1} fontWeight="semibold">
                          Invalid file type
                        </Text>
                        <Text color="red.700">Please upload a valid JSON file</Text>
                      </Box>
                      <Button
                        variant="link"
                        color="blue.500"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUploadClick();
                        }}
                      >
                        Try again
                      </Button>
                    </VStack>
                  ) : (
                    <VStack spacing={3}>
                      <Icon as={LuUpload} w={12} h={12} color="gray.400" />
                      <Box>
                        <Text color="gray.700" mb={1}>
                          Drag and drop your Firebase Admin SDK JSON file here
                        </Text>
                        <Text color="gray.500">or</Text>
                      </Box>
                      <Button
                        leftIcon={<Icon as={LuUpload} />}
                        bg="var(--brand-primary)"
                        color="white"
                        _hover={{ bg: 'var(--brand-primary-hover)' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUploadClick();
                        }}
                        isLoading={isUploading}
                        loadingText="Uploading"
                      >
                        Browse Files
                      </Button>
                    </VStack>
                  )}
                </Box>

                <Input
                  type="file"
                  accept="application/json"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  display="none"
                />
              </Box>
              {isConfigured && (<Text fontSize="lg" color="green.600" fontWeight="semibold" textAlign={"center"}>
                Your SDK JSON file is uploaded. Choose a new file to override.
              </Text>)}
              {/* Guide Section */}
              <Box>
                <Text fontSize="xl" mb={4}>
                  Guide: Generating and Uploading Firebase Admin SDK for Push Notification Integration
                </Text>
                <Text mb={4}>
                  To enable Push Notification functionality, the Firebase Admin SDK JSON file must be generated and uploaded into our application. This guide provides a step-by-step process for generating and securely integrating the file into our system.
                </Text>

                {/* Step 1 */}
                <Box>
                  <Text fontSize="xl" mb={4}>
                    Step 1: Generate Firebase Admin SDK JSON File
                  </Text>

                  <OrderedList spacing={4}>
                    <ListItem>
                      <Text as='b'>Go to Firebase Console</Text>
                      <UnorderedList spacing={2} mt={2}>
                        <ListItem>
                          Open <Link to="#" style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}>Firebase Console</Link>
                        </ListItem>
                        <ListItem>
                          Select your project (or create a new one)
                        </ListItem>
                      </UnorderedList>
                    </ListItem>

                    <ListItem>
                      <Text as='b'>Access Project Settings</Text>
                      <UnorderedList spacing={2} mt={2}>
                        <ListItem>
                          Click on the ⚙️ <strong>Settings icon</strong> in the left sidebar
                        </ListItem>
                        <ListItem>
                          Select <strong>"Project Settings"</strong> from the dropdown menu
                        </ListItem>
                      </UnorderedList>
                    </ListItem>

                    <ListItem>
                      <Text as='b'>Download the Admin SDK JSON File</Text>
                      <UnorderedList spacing={2} mt={2}>
                        <ListItem>
                          Navigate to the <strong>"Service accounts"</strong> tab
                        </ListItem>
                        <ListItem>
                          Click <strong>"Generate new private key"</strong> under the Firebase Admin SDK section
                        </ListItem>
                        <ListItem>
                          A JSON file will be automatically downloaded to your system
                        </ListItem>
                      </UnorderedList>
                    </ListItem>
                  </OrderedList>
                </Box>

                {/* Step 2 */}
                <Box mt={3}>
                  <Text size="sm" mb={3}>Step 2: Upload the JSON File</Text>
                  <Text mb={4}>
                    Once the <strong>JSON file</strong> is generated, it must be uploaded into our application to enable Firebase authentication.
                  </Text>
                  <List spacing={3}>
                    <ListItem display="flex" alignItems="center" gap={2}>
                      <Icon as={DownloadIcon} color="blue.500" />
                      <Text>Click on the Upload File Button</Text>
                    </ListItem>
                  </List>
                </Box>

                {/* Security Warning */}
                <Box
                  mt={4}
                  mb={8}
                  p={4}
                  bg="#fff7ed"
                  border="1px solid"
                  borderColor="#ff8904"
                  borderRadius="lg"
                >
                  <HStack align="flex-start" spacing={3}>
                    <Icon as={WarningIcon} color="orange.600" boxSize={5} mt={0.5} />
                    <Box>
                      <Text color="orange.800" fontWeight="semibold" mb={1}>
                        Important Security Information
                      </Text>
                      <Text color="orange.700">
                        Ensure this file is never exposed publicly (e.g., avoid committing it to GitHub).
                      </Text>
                    </Box>
                  </HStack>
                </Box>
              </Box>
            </VStack>
  );

  if (embedded) {
    return (
      <Box>
        <Text fontSize="2xl" fontWeight="semibold">
          Configure Push Notification
        </Text>
        <Divider my={2} />
        {content}
      </Box>
    );
  }

  return (
    <>
      <Header header={header} />
      <ToastContainer />
      <BoxWrapper>
        <Flex justify={"center"} align={"center"}>
          <HStack w={"70%"} mt={8}>
            <Button
              onClick={handleBack}
              as="label"
              leftIcon={<Icon as={LuArrowLeft} />}
              color={"black"}
              variant="outline"
              colorScheme="whiteAlpha"
              cursor="pointer"
              size={"sm"}
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              sx={{
                "&:hover": {
                  background: "#3131311a !important",
                  color: "black !important",

                },
              }}
            >
              Back
            </Button>
            <Text fontSize="32px" fontWeight="300" color="#000000" my="0px" >
              Configure Push Notification
            </Text>
            {/* <Tag bg={"#7f52ff"} color={"white"} fontSize={"xs"}>Kotlin</Tag> */}
          </HStack>
        </Flex>
        <Divider bg={"#eaeaea"} my={8} />
        <Flex justify={"center"} align="center">
          <Box w={"70%"}>
            {content}
          </Box>
        </Flex>

      </BoxWrapper>
    </>
  );
};

export default ConfigurePushNotification;